import {
  arrayUnion,
  deleteDoc,
  doc,
  type Firestore,
  getDoc,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import { v4 } from 'uuid';
import type { Invite } from '@/service/invite/types';

export default class InviteClient {
  constructor(
    private readonly firestore: Firestore,
    private readonly uid: string,
  ) {}

  async createFamilyInvite(): Promise<Invite> {
    const invite: Invite = {
      id: v4(),
      type: 'family',
      fromUid: this.uid,
      createdAt: Date.now(),
    };
    await setDoc(doc(this.firestore, 'invites', invite.id), invite);
    return invite;
  }

  async createRecipeInvite(recipeId: string): Promise<Invite> {
    const invite: Invite = {
      id: v4(),
      type: 'recipe',
      fromUid: this.uid,
      recipeId,
      createdAt: Date.now(),
    };
    await setDoc(doc(this.firestore, 'invites', invite.id), invite);
    return invite;
  }

  async getInvite(id: string): Promise<Invite | undefined> {
    const snapshot = await getDoc(doc(this.firestore, 'invites', id));
    if (snapshot.exists()) {
      return snapshot.data() as Invite;
    }
    return undefined;
  }

  // Accepting writes name the invite (`acceptedInvite`) and consume it in the
  // same batch; the security rules only allow the change with a valid invite.
  async acceptFamilyInvite(invite: Invite): Promise<void> {
    const batch = writeBatch(this.firestore);
    batch.update(doc(this.firestore, 'groups', invite.fromUid), {
      users: arrayUnion(this.uid),
      acceptedInvite: invite.id,
    });
    batch.update(doc(this.firestore, 'groups', this.uid), {
      users: arrayUnion(invite.fromUid),
      acceptedInvite: invite.id,
    });
    batch.delete(doc(this.firestore, 'invites', invite.id));
    await batch.commit();
  }

  async acceptRecipeInvite(invite: Invite): Promise<void> {
    if (!invite.recipeId) {
      throw new Error('Recipe invite is missing recipeId');
    }
    const batch = writeBatch(this.firestore);
    batch.update(doc(this.firestore, 'recipes', invite.recipeId), {
      sharedWith: arrayUnion(this.uid),
      acceptedInvite: invite.id,
    });
    batch.delete(doc(this.firestore, 'invites', invite.id));
    await batch.commit();
  }

  async deleteInvite(id: string): Promise<void> {
    await deleteDoc(doc(this.firestore, 'invites', id));
  }
}
